import { LabelType } from "constants/enum";
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from "constants/index";

import React, { FC, ForwardedRef, useEffect, useState } from "react";

import AutocompleteHookForm from "components/Autocomplete/Autocomplete.hookform";
import { MultiAutocompleteHookForm } from "components/MultiAutocomplete";
import {
  ClientEntity,
  useGetManyBaseClientControllerClientEntity,
} from "generated";
import { useDebounce } from "hooks/useDebounce";

export interface SelectProps {
  name: string;
  label: string;
  labelType?: LabelType;
  required?: boolean;
  multi?: boolean;
  disabled?: boolean;
  templateId?: string;
  onChange?: (value: any) => void;
  placeholder?: string;
}

type ClientSelectFormProps = SelectProps;

const ClientSelectForm: FC<ClientSelectFormProps> = React.forwardRef<
  HTMLSelectElement,
  ClientSelectFormProps
>((props: ClientSelectFormProps, ref: ForwardedRef<HTMLSelectElement>) => {
  const {
    name,
    label,
    labelType,
    required: require,
    multi,
    disabled,
    templateId,
    onChange,
    placeholder,
  } = props;

  const [keyword, setKeyword] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [listOptions, setListOptions] =
    useState<{ label: string; value: unknown }[]>();

  const { data } = useGetManyBaseClientControllerClientEntity({
    //eslint-disable-line
    s: JSON.stringify({
      ...(keyword
        ? {
            name: {
              $contL: keyword,
            },
          }
        : {}),
      ...(templateId
        ? {
            "templates.id": {
              $eq: templateId,
            },
          }
        : {}),
    }),
    join: ["templates"],
    limit: DEFAULT_PAGE_SIZE,
    offset: DEFAULT_PAGE_INDEX,
  });

  // debounce
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  useEffect(() => {
    setKeyword(debouncedSearchTerm);
  }, [debouncedSearchTerm]);

  const getLabelType = (item: ClientEntity) => {
    if (labelType) {
      switch (labelType) {
        case LabelType.name:
          return item.name;
        case LabelType.email:
          return item.email;
        case LabelType.legalName:
          return item.legalName;
        default:
          return item.name;
      }
    }
    return item?.name;
  };
  const handleSearchSelection = (value: string) => {
    setSearchTerm(value);
  };
  const handleResetData = (dataQuery: ClientEntity[]) => {
    let options: any[] = [];
    if (dataQuery && dataQuery.length > 0) {
      options = dataQuery.map((item) => ({
        value: item?.id,
        label: getLabelType(item),
        info: item,
      }));
    }
    setListOptions(options);
  };
  useEffect(() => {
    handleResetData(data?.data?.data ?? []); //eslint-disable-line
  }, [data]); //eslint-disable-line

  return multi ? (
    <MultiAutocompleteHookForm
      name={name}
      label={label}
      placeholder={placeholder}
      options={listOptions ?? []}
      onInputChange={handleSearchSelection}
      required={require}
      disabled={disabled}
    />
  ) : (
    <AutocompleteHookForm
      name={name}
      label={label}
      placeholder="Select an option"
      options={listOptions ?? []}
      onInputChange={handleSearchSelection}
      required={require}
      disabled={disabled}
      onChange={onChange}
    />
  );
});
export default ClientSelectForm;
